import React, { useEffect, useState, useRef } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const metrics = [{
  id: 1,
  value: 10,
  suffix: "M+",
  prefix: "$",
  label: "Capital Accessed",
  description: "Facilitating financial growth for local businesses."
}, {
  id: 2,
  value: 5000,
  suffix: "+",
  prefix: "",
  label: "Entrepreneurs Supported",
  description: "Empowering the next generation of business leaders."
}, {
  id: 3,
  value: 1200,
  suffix: "+",
  prefix: "",
  label: "Jobs Created",
  description: "Strengthening the local workforce and economy."
}, {
  id: 4,
  value: 45,
  suffix: " Years",
  prefix: "",
  label: "Of Community Impact",
  description: "Serving Colorado's Hispanic community since 1978."
}];
const Counter = ({
  end,
  duration = 2000,
  prefix = "",
  suffix = "",
  className
}: {
  end: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
}) => {
  const [count, setCount] = useState(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.5
  });
  const [hasStarted, setHasStarted] = useState(false);
  useEffect(() => {
    if (isVisible && !hasStarted) {
      setHasStarted(true);
      let startTime: number | null = null;
      const step = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };
      window.requestAnimationFrame(step);
    }
  }, [isVisible, end, duration, hasStarted]);
  return <span ref={ref as React.RefObject<HTMLSpanElement>} className={className}>
      {prefix}
      {count.toLocaleString()}
      {suffix}
    </span>;
};
export default function MetricsOfProgress() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="metrics-of-progress" className="dark bg-primary text-foreground py-24 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-secondary/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-[20%] -right-[10%] w-[50%] h-[50%] bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {}
        <div ref={sectionRef as React.RefObject<HTMLDivElement>} className={cn("relative rounded-3xl border border-secondary/30 bg-primary/40 backdrop-blur-md p-8 md:p-16 transition-all duration-1000 ease-out", isSectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          {}
          <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-secondary rounded-tl-xl -mt-px -ml-px" />
          <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-secondary rounded-tr-xl -mt-px -mr-px" />
          <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-secondary rounded-bl-xl -mb-px -ml-px" />
          <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-secondary rounded-br-xl -mb-px -mr-px" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 divide-y md:divide-y-0 md:divide-x divide-secondary/20">
            {metrics.map((metric, i) => <div key={metric.id} data-index={i} className="flex flex-col items-center text-center px-4 pt-8 md:pt-0 first:pt-0">
                <div className="mb-4">
                  <Counter end={metric.value} prefix={metric.prefix} suffix={metric.suffix} className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-secondary tracking-tight" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2 font-sans uppercase tracking-wider">
                  {metric.label}
                </h3>
                <p className="text-sm text-muted-foreground font-sans max-w-[200px] leading-relaxed">
                  {metric.description}
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}